/**
 * V121: 추가근무 날짜 기준 프로젝트 목록 조회 Hook
 *
 * 추가근무 신청 시 선택할 수 있는 프로젝트 목록을 조회합니다.
 * - 선택된 날짜 기준으로 투입된 프로젝트만 반환
 * - 'NON_PROJECT' 옵션 포함 (공통업무/비프로젝트)
 *
 * @module shared/hooks/useAssignableProjects
 */

import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';

/**
 * 프로젝트 항목
 */
export interface AssignableProject {
  projectId: string;
  projectName: string;
}

/**
 * getAssignableProjectsForOvertime 응답 타입
 */
interface GetAssignableProjectsResponse {
  success: boolean;
  projects: AssignableProject[];
  error?: string;
}

/**
 * Hook 반환 타입
 */
export interface UseAssignableProjectsResult {
  /** 투입 프로젝트 목록 (NON_PROJECT 포함) */
  projects: AssignableProject[];
  /** 로딩 상태 */
  isLoading: boolean;
  /** 에러 */
  error: Error | null;
  /** 수동 새로고침 */
  refetch: () => void;
}

/**
 * V121: 추가근무 날짜 기준 프로젝트 목록 조회 Hook
 *
 * @param date - 추가근무 날짜 (YYYY-MM-DD)
 * @returns 프로젝트 목록 및 상태
 *
 * @example
 * ```typescript
 * function OvertimeForm() {
 *   const [date, setDate] = useState('2026-01-30');
 *   const { projects, isLoading } = useAssignableProjects(date);
 *
 *   return (
 *     <select>
 *       {projects.map(p => (
 *         <option key={p.projectId} value={p.projectId}>{p.projectName}</option>
 *       ))}
 *     </select>
 *   );
 * }
 * ```
 */
export function useAssignableProjects(date: string): UseAssignableProjectsResult {
  const [projects, setProjects] = useState<AssignableProject[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchProjects = useCallback(async () => {
    // 날짜 형식 검증
    if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      setProjects([{ projectId: 'NON_PROJECT', projectName: '공통업무 (비프로젝트)' }]);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const functions = getFunctions();
      const getAssignableProjectsFn = httpsCallable<
        { date: string },
        GetAssignableProjectsResponse
      >(functions, 'getAssignableProjectsForOvertime');

      const result = await getAssignableProjectsFn({ date });

      if (result.data.success) {
        // 프로젝트 목록 + NON_PROJECT 옵션 추가
        const projectList: AssignableProject[] = [
          ...result.data.projects,
          { projectId: 'NON_PROJECT', projectName: '공통업무 (비프로젝트)' },
        ];
        setProjects(projectList);
      } else {
        throw new Error(result.data.error || '프로젝트 목록 조회 실패');
      }
    } catch (err: any) {
      console.error('[V121] useAssignableProjects error:', err);
      setError(err);
      // 에러 시에도 NON_PROJECT 옵션은 제공
      setProjects([{ projectId: 'NON_PROJECT', projectName: '공통업무 (비프로젝트)' }]);
    } finally {
      setIsLoading(false);
    }
  }, [date]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  return {
    projects,
    isLoading,
    error,
    refetch: fetchProjects,
  };
}
